import { type CSSProperties } from "react";
import { motion } from "framer-motion";
import { Star, Shield, Zap, CheckCircle, Phone, Sparkles, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import HeroEstimateForm from "@/components/HeroEstimateForm";

const badges = [
  { icon: Shield, label: "Honest Service" },
  { icon: Star, label: "Quality Work" },
  { icon: Zap, label: "Fast Response" },
  { icon: CheckCircle, label: "Lasting Results" },
];

const stats = [
  { value: "8", label: "Core Services", suffix: "" },
  { value: "9", label: "Cities Served", suffix: "+" },
  { value: "100", label: "Satisfaction Focus", suffix: "%" },
];

const HeroSection = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      <img
        src="/images/hero-home.webp"
        alt="Freshly painted home interior by Villalobos Prime Services"
        className="absolute inset-0 w-full h-full object-cover animate-bg-zoom"
        loading="eager"
        fetchPriority="high"
        width={500}
        height={437}
      />
      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(222,35%,6%/0.6)] via-[hsl(222,35%,8%/0.5)] to-[hsl(218,45%,12%/0.4)]" />
      <div className="absolute inset-0 gradient-mesh opacity-30" />

      <div
        aria-hidden="true"
        className="hero-blob absolute top-20 right-20 w-96 h-96 rounded-full"
        style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.3), transparent 70%)", animationDuration: "6s" }}
      />
      <div
        aria-hidden="true"
        className="hero-blob absolute bottom-40 left-10 w-64 h-64 rounded-full"
        style={{
          background: "radial-gradient(circle, hsl(var(--secondary) / 0.3), transparent 70%)",
          animationDuration: "8s",
          animationDelay: "2s",
          ["--blob-scale"]: "1.15",
          ["--blob-op-min"]: "0.1",
          ["--blob-op-max"]: "0.2",
        } as CSSProperties}
      />

      <div className="relative z-10 container-max px-4 py-32 md:py-44 lg:py-52 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="inline-flex items-center gap-2 bg-primary/15 backdrop-blur-sm border border-primary/30 text-white text-sm font-semibold px-5 py-2 rounded-full mb-10"
              >
                <Sparkles className="h-4 w-4 text-secondary" />
                Painting & Remodeling in Deltona, FL
              </motion.div>

              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-white leading-[0.95] mb-8 tracking-tight font-bold">
                Quality Work.
                <br />
                <span className="text-secondary">Honest</span> Service.
                <br />
                Lasting Results.
              </h1>
              <p className="text-white/80 text-lg md:text-xl mb-10 leading-relaxed max-w-xl">
                Villalobos Prime Services LLC delivers professional painting, flooring, drywall, cabinet, and remodeling services to homeowners across Deltona and Central Florida.
              </p>

              <div className="flex flex-wrap items-center gap-4 mb-10">
                <Button
                  size="lg"
                  onClick={openQuoteForm}
                  className="text-base gap-2 px-8 py-6 text-lg rounded-xl shadow-[0_18px_40px_-12px_hsl(var(--primary)/0.55)]"
                >
                  Get Free Estimate
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  asChild
                  className="text-base gap-2 px-8 py-6 text-lg rounded-xl border-2 border-white/25 text-white bg-white/5 hover:bg-white/15 hover:text-white hover:border-white/40 backdrop-blur-sm"
                >
                  <a href="tel:7863065704"><Phone className="h-5 w-5" /> (786) 306-5704</a>
                </Button>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="glass-card rounded-xl px-5 py-3 inline-flex items-center gap-3"
              >
                <Gift className="h-6 w-6 text-secondary" />
                <span className="text-white/90 font-semibold text-sm">New clients get <span className="text-secondary font-bold">10% OFF</span> their first project!</span>
              </motion.div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.7 }}
            className="lg:col-span-5 relative"
          >
            <div
              aria-hidden="true"
              className="absolute -inset-6 rounded-[2rem] opacity-60 blur-2xl pointer-events-none -z-10"
              style={{ background: "radial-gradient(60% 60% at 50% 50%, hsl(var(--primary) / 0.35), transparent 70%)" }}
            />
            <HeroEstimateForm />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-16 md:mt-20 glass-card rounded-2xl p-6 md:p-8 gradient-border"
        >
          <div className="flex flex-wrap items-center justify-around gap-6">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + i * 0.15, duration: 0.5 }}
                className="text-center"
              >
                <div className="font-heading text-3xl md:text-4xl text-white tracking-wide font-bold">
                  {stat.value}<span className="text-secondary">{stat.suffix}</span>
                </div>
                <div className="text-white/75 text-xs font-semibold uppercase tracking-wider mt-1">{stat.label}</div>
              </motion.div>
            ))}
            {badges.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-secondary" />
                </div>
                <span className="text-white/80 text-xs font-medium">{label}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <div className="pointer-events-none absolute -bottom-px left-0 right-0 h-16 bg-background leading-none">
        <svg viewBox="0 0 1440 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="block w-full h-full" preserveAspectRatio="none">
          <path d="M0 32C240 64 480 64 720 32C960 0 1200 0 1440 32V64H0V32Z" fill="hsl(var(--background))" />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
